import { reactive } from 'vue';

const state = reactive({
  popovers: {}
});

function ensurePopover (name) {
  if (!state.popovers[name]) {
    state.popovers[name] = {
      visible: false,
      hideTimer: null
    };
  }
  return state.popovers[name];
}

function clearHideTimer (name) {
  const popover = ensurePopover(name);
  if (popover.hideTimer) {
    clearTimeout(popover.hideTimer);
    popover.hideTimer = null;
  }
}

const popoverApi = {
  state,
  show (name) {
    if (!name) return;
    Object.keys(state.popovers).forEach(key => {
      if (key !== name) {
        this.hide(key, true);
      }
    });
    const popover = ensurePopover(name);
    clearHideTimer(name);
    popover.visible = true;
  },
  hide (name, immediate = false) {
    if (!name) return;
    const popover = ensurePopover(name);
    clearHideTimer(name);
    if (immediate) {
      popover.visible = false;
      return;
    }
    popover.hideTimer = setTimeout(() => {
      popover.visible = false;
      popover.hideTimer = null;
    }, 120);
  },
  toggle (name) {
    const popover = ensurePopover(name);
    if (popover.visible) {
      this.hide(name, true);
    } else {
      this.show(name);
    }
  },
  isVisible (name) {
    return !!(state.popovers[name] && state.popovers[name].visible);
  },
  keepOpen (name) {
    if (!name) return;
    clearHideTimer(name);
    ensurePopover(name).visible = true;
  }
};

function getBindingOptions (binding) {
  if (typeof binding.value === 'string') {
    return { name: binding.value };
  }
  if (binding.value && typeof binding.value === 'object') {
    return binding.value;
  }
  if (binding.arg) {
    return { name: binding.arg };
  }
  return {};
}

function bindPopover (el, binding) {
  unbindPopover(el);

  const options = getBindingOptions(binding);
  const eventType = options.event || 'click';
  const name = options.name || binding.arg;

  if (!name) {
    return;
  }

  const onClick = function (event) {
    if (event && event.preventDefault) {
      event.preventDefault();
    }
    popoverApi.toggle(name);
  };

  const onMouseEnter = function () {
    popoverApi.show(name);
  };

  const onMouseLeave = function () {
    popoverApi.hide(name);
  };

  const handlers = {
    name,
    eventType,
    onClick,
    onMouseEnter,
    onMouseLeave
  };

  el.__popoverHandlers__ = handlers;

  if (eventType === 'hover') {
    el.addEventListener('mouseenter', onMouseEnter);
    el.addEventListener('mouseleave', onMouseLeave);
  } else {
    el.addEventListener('click', onClick);
  }
}

function unbindPopover (el) {
  const handlers = el.__popoverHandlers__;
  if (!handlers) {
    return;
  }

  if (handlers.eventType === 'hover') {
    el.removeEventListener('mouseenter', handlers.onMouseEnter);
    el.removeEventListener('mouseleave', handlers.onMouseLeave);
  } else {
    el.removeEventListener('click', handlers.onClick);
  }

  delete el.__popoverHandlers__;
}

export function getPopoverApi () {
  return popoverApi;
}

export const popoverDirective = {
  beforeMount: bindPopover,
  mounted: bindPopover,
  updated: bindPopover,
  beforeUnmount: unbindPopover,
  unmounted: unbindPopover,
  bind: bindPopover,
  unbind: unbindPopover
};

export default {
  install (app) {
    app.directive('popover', popoverDirective);
    app.config.globalProperties.$popover = popoverApi;
  }
};
